import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { RootState } from '@/store'
import axios, { AxiosRequestConfig } from 'axios'
import {
	Referral,
	ReferralsMeta,
	ReferralsResponse,
	ReferralType,
} from '@/modules/referrals/types/referralTypes'
import { error } from '@/components/common/NotificationPlugin'

export type State = {
	/** The zip searched from — pre-filled from the client, then owned by the card's input. */
	postalCode: string
	/** Set when the API rejects `postalCode`; shown against the card's input. */
	postalCodeError: string | null
	referrals: Referral[]
	meta: ReferralsMeta | null
	loading: boolean
}

export const state = (): State => ({
	postalCode: '',
	postalCodeError: null,
	referrals: [],
	meta: null,
	loading: false,
})

interface SearchPayload {
	types?: ReferralType[]
	/** Undefined searches both virtual and in-person providers. */
	isVirtual?: boolean
	page?: number
}

const mutations: MutationTree<State> = {
	// BaseInput emits null when it's cleared, so normalise here and keep the rest of
	// the module (and every getter reading it) on a plain string.
	setPostalCode(state: State, postalCode: string | null) {
		state.postalCode = postalCode ?? ''
		state.postalCodeError = null
	},
	setPostalCodeError(state: State, postalCodeError: string | null) {
		state.postalCodeError = postalCodeError
	},
	setReferrals(state: State, referrals: Referral[]) {
		state.referrals = referrals
	},
	appendReferrals(state: State, referrals: Referral[]) {
		state.referrals = [...state.referrals, ...referrals]
	},
	setMeta(state: State, meta: ReferralsMeta | null) {
		state.meta = meta
	},
	setLoading(state: State, loading: boolean) {
		state.loading = loading
	},
}

async function fetchReferrals(
	postalCode: string,
	{ types = [], isVirtual, page = 1 }: SearchPayload,
): Promise<ReferralsResponse> {
	// The response interceptor unwraps the HTTP body, leaving `{ data, meta }`.
	const unwrapped = await axios.get('/referrals', {
		params: {
			postal_code: postalCode,
			types,
			// Laravel's `boolean` rule rejects the "true"/"false" axios serialises a bool to.
			is_virtual: isVirtual === undefined ? undefined : Number(isVirtual),
			page,
		},
		// A rejected zip belongs against the card's input, not in a global toast.
		showsErrorsItself: true,
	} as AxiosRequestConfig)

	return unwrapped as unknown as ReferralsResponse
}

/** The API's message for an unrecognised zip, or null when it failed for another reason. */
function postalCodeErrorFrom(err: any): string | null {
	return err?.response?.data?.errors?.postal_code?.[0] ?? null
}

const actions: ActionTree<State, RootState> = {
	async findReferrals({ commit, state }, payload: SearchPayload = {}): Promise<void> {
		if (!state.postalCode) {
			return
		}

		try {
			commit('setLoading', true)

			const { data, meta } = await fetchReferrals(state.postalCode, { ...payload, page: 1 })

			commit('setPostalCodeError', null)
			commit('setReferrals', data)
			commit('setMeta', meta)
		} catch (err: any) {
			commit('setReferrals', [])
			commit('setMeta', null)
			commit('setPostalCodeError', postalCodeErrorFrom(err))

			// Opting out of the global toast covers the zip, not everything else that
			// can go wrong, so anything unrelated still has to announce itself.
			if (!state.postalCodeError) {
				error('We couldn’t load providers just now. Please try again.')
			}
		} finally {
			commit('setLoading', false)
		}
	},
	clearReferrals({ commit }): void {
		commit('setReferrals', [])
		commit('setMeta', null)
	},
	async loadMoreReferrals({ commit, state }, payload: SearchPayload = {}): Promise<void> {
		if (!state.meta?.has_more) {
			return
		}

		try {
			commit('setLoading', true)

			const { data, meta } = await fetchReferrals(state.postalCode, {
				...payload,
				page: state.meta.page + 1,
			})

			commit('appendReferrals', data)
			commit('setMeta', meta)
		} catch {
			error('We couldn’t load more providers just now. Please try again.')
		} finally {
			commit('setLoading', false)
		}
	},
}

const getters: GetterTree<State, RootState> = {
	postalCode: (state: State) => state.postalCode,
	postalCodeError: (state: State) => state.postalCodeError,
	hasPostalCode: (state: State) => state.postalCode.trim().length > 0,
	canSearch: (state: State, getters) => getters.hasPostalCode && !state.postalCodeError,
	referrals: (state: State) => state.referrals,
	hasMore: (state: State) => state.meta?.has_more ?? false,
	loading: (state: State) => state.loading,
}

const module: Module<State, RootState> = {
	namespaced: true,
	state,
	mutations,
	actions,
	getters,
}

export default module
